import { useEffect, useState } from "react"
import {
  collection,
  DocumentData,
  FirestoreError,
  onSnapshot,
} from "firebase/firestore"
import { useFirebase } from "@/firebase/FirebaseProvider"
import { query, QueryConstraint } from "@firebase/firestore"
import { notifyBugsnag } from "@/support/bugsnag/bugsnags"

// Handles notifying bugsnag on error
export default function useCollectionSnapshots<
  T extends DocumentData = DocumentData,
>({
  path,
  enabled = true,
  constraints = [],
  mapResult,
}: {
  path: string
  enabled?: boolean
  constraints?: QueryConstraint[]
  // called before the data is set to the state
  // useful for sorting or filtering
  mapResult?: (docs: T[]) => T[]
}) {
  const { firestore } = useFirebase()

  // only start with isLoading if enabled is true
  const [isLoading, setIsLoading] = useState(enabled)
  const [data, setData] = useState<T[] | undefined>()
  const [error, setError] = useState<FirestoreError | Error | undefined>()

  // need to stringify constraints to compare them
  const constraintsKey = JSON.stringify(constraints)

  useEffect(
    () => {
      setIsLoading(true)
      setError(undefined)
      setData(undefined)

      if (!enabled || !path) {
        setIsLoading(false)
        return
      }

      const isMounted = { current: true }

      try {
        const collectionReference = collection(firestore, path)
        const q = constraints.length
          ? query(collectionReference, ...constraints)
          : collectionReference

        const unsubscribe = onSnapshot(q, {
          next: (snapshot) => {
            if (!isMounted.current) return

            let docs = snapshot.docs.map((doc) => doc.data() as T)
            if (mapResult) {
              docs = mapResult(docs)
            }
            setData(docs)
            setIsLoading(false)
          },
          error: (e) => {
            notifyBugsnag(e)
            if (!isMounted.current) return

            setError(e)
            setIsLoading(false)
          },
        })

        return () => {
          isMounted.current = false
          unsubscribe()
        }
      } catch (e) {
        notifyBugsnag(e)
        if (isMounted.current) {
          setError(e instanceof Error ? e : new Error(String(e)))
          setIsLoading(false)
        }
        return () => {
          isMounted.current = false
        }
      }
    },
    // constraintsKey is covering the constraints array
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [path, enabled, firestore, constraintsKey],
  )

  return { isLoading, data, error }
}
